'use client'

import { useEffect, useState } from 'react'
import { useSearchParams } from 'next/navigation'
import { SWRConfig } from 'swr'
import {
  useQueryState,
  QueryStateProvider,
  useResponsive,
  useTranslations,
} from '@dustin-web/microsite-components/src/hooks'
import { getSortBy, useProductListSettings } from '@dustin-web/product-components/src/product-list'
import { useGraphql } from '@dustin-web/microsite-data/src/hooks'
import useCleanRouter from '@dustin-web/microsite-components/src/hooks/use-clean-router'
import type {
  QuerySearchPageArgs,
  SearchPageQuery,
} from '@productsite/src/graphql/generated/graphql-types'
import { Origin, Touchpoint } from '@productsite/src/graphql/generated/graphql-types'
import { infinityScrollPageSize } from '@productsite/src/components/utils/infinity-scroll/use-infinity-scroll'
import { SearchPage as GetSearchPage } from '@productsite/src/graphql/search.graphql'
import SearchPage from './search-page'
import { getCategoryPathFromPath } from '@productsite/src/utils/get-category-path-from-path'
import { decodeSearchPhrase } from '@productsite/src/utils/decode-search-phrase'

export type SearchPageProps = {
  initialData: SearchPageQuery | null
  searchPhrase: string
  isUserInitiated: boolean
  fallback?: { [key: string]: any }
}

const SearchPageContent = ({
  initialData,
  searchPhrase,
  isUserInitiated,
}: SearchPageProps) => {
  const { queryState } = useQueryState()
  const { isSmallScreen } = useResponsive()
  const { sortBy } = useProductListSettings()
  const { translate } = useTranslations()
  const { pageUriWithoutQuery } = useCleanRouter()

  const { activeFilters: facets, page } = queryState
  const selectedCategoryPath = getCategoryPathFromPath(pageUriWithoutQuery)
  const phrase = pageUriWithoutQuery.split('/')[2] || searchPhrase 
  const decodedSearchPrase = phrase ? decodeSearchPhrase(phrase) : ''
  const isValidSearch = !!decodedSearchPrase

  const { isValidating: loading, data: fetchedData } = useGraphql<
    SearchPageQuery,
    QuerySearchPageArgs
  >(GetSearchPage, {
    variables: {
      isUserInitiated,
      page,
      pageSize: infinityScrollPageSize,
      searchPhrase: decodedSearchPrase,
      selectedCategoryPath,
      origin: Origin.Organic,
      touchpoint: (isSmallScreen && Touchpoint.Mobile) || Touchpoint.Desktop,
      facets: facets,
      sortBy: getSortBy(sortBy),
    },
    context: {
      skip: !isValidSearch,
    },
    swrOptions: {
      ...(initialData ? { fallbackData: initialData } : {}),
    },
  })

  const [data, setData] = useState(fetchedData?.searchPage ?? initialData?.searchPage ?? null)

  useEffect(() => {
    if (!loading && fetchedData) {
      setData(fetchedData.searchPage)
    }
  }, [loading, setData, fetchedData])

  if (!data || !isValidSearch) {
    return (<div className="container"><span className='d-block h2 u-pt-24 u-pb-24'>{translate('InvalidSearch')}</span></div>)
  }

  return (
      <SearchPage
        searchPage={data}
        loading={loading}
        searchPhrase={decodedSearchPrase}
        selectedCategoryPath={selectedCategoryPath}
        isUserInitiated={isUserInitiated}
      />
  )
}

/**
 * Component for the search page.
 * Handles user-initiated search refinement, facet filtering, and result display.
 * Wraps the search page logic with QueryStateProvider and SWRConfig for fallback data.
 */
export default function SearchPageLayout({ fallback, ...props }: SearchPageProps) {
  const searchParams = useSearchParams()
  const query = searchParams ? Object.fromEntries(searchParams.entries()) : {}

  return (
    <SWRConfig value={{ fallback }}>
      <QueryStateProvider query={query}>
        <SearchPageContent {...props} />
      </QueryStateProvider>
    </SWRConfig>
  )
}
